import { supabase } from './supabase';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_CLIENT_SECRET = import.meta.env.VITE_GOOGLE_CLIENT_SECRET;
const GOOGLE_REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI;

// Scopes needed for Google Fit API
const SCOPES = [
  'https://www.googleapis.com/auth/fitness.activity.read',
  'https://www.googleapis.com/auth/fitness.heart_rate.read',
  'https://www.googleapis.com/auth/fitness.sleep.read',
  'https://www.googleapis.com/auth/fitness.body.read'
].join(' ');

// Generate auth URL for OAuth flow
export function getAuthUrl(): string {
  if (!GOOGLE_CLIENT_ID) {
    throw new Error('Google Client ID not configured');
  }

  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline',
    prompt: 'consent',
    include_granted_scopes: 'true'
  });

  console.log('Generated auth URL with params:', {
    clientId: GOOGLE_CLIENT_ID,
    redirectUri: GOOGLE_REDIRECT_URI,
    scopes: SCOPES
  });

  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

// Exchange code for tokens
export async function getAccessToken(code: string) {
  if (!GOOGLE_CLIENT_ID || !GOOGLE_CLIENT_SECRET) {
    throw new Error('Google credentials not configured');
  }

  console.log('Exchanging code for tokens...');

  try {
    const tokenEndpoint = 'https://oauth2.googleapis.com/token';
    const body = new URLSearchParams({
      code,
      client_id: GOOGLE_CLIENT_ID,
      client_secret: GOOGLE_CLIENT_SECRET,
      redirect_uri: GOOGLE_REDIRECT_URI,
      grant_type: 'authorization_code'
    });

    console.log('Token request params:', {
      code: code.substring(0, 10) + '...',
      clientId: GOOGLE_CLIENT_ID,
      redirectUri: GOOGLE_REDIRECT_URI
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: body.toString()
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Token exchange error:', errorData);
      throw new Error(errorData.error_description || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Token exchange successful');

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in
    };
  } catch (error) {
    console.error('Error getting access token:', error);
    throw error;
  }
}

// Refresh access token
async function refreshAccessToken(refreshToken: string) {
  if (!GOOGLE_CLIENT_ID || !GOOGLE_CLIENT_SECRET) {
    throw new Error('Google credentials not configured');
  }

  console.log('Refreshing access token...');

  try {
    const tokenEndpoint = 'https://oauth2.googleapis.com/token';
    const body = new URLSearchParams({
      client_id: GOOGLE_CLIENT_ID,
      client_secret: GOOGLE_CLIENT_SECRET,
      refresh_token: refreshToken,
      grant_type: 'refresh_token'
    });

    const response = await fetch(tokenEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Accept': 'application/json'
      },
      body: body.toString()
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Token refresh error:', errorData);
      throw new Error(errorData.error_description || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Token refresh successful');
    return data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
}

// Save tokens to Supabase
export async function saveTokens(userId: string, tokens: any) {
  console.log('Saving tokens to database...');

  try {
    const { error } = await supabase
      .from('google_fit_tokens')
      .upsert({
        user_id: userId,
        access_token: tokens.access_token,
        refresh_token: tokens.refresh_token,
        expiry_date: new Date(Date.now() + (tokens.expires_in * 1000)).toISOString(),
        updated_at: new Date().toISOString()
      });

    if (error) {
      console.error('Error saving tokens to database:', error);
      throw error;
    }

    console.log('Tokens saved successfully');
  } catch (error) {
    console.error('Error saving tokens:', error);
    throw error;
  }
}

// Get valid access token (refreshes if expired)
async function getValidAccessToken(userId: string): Promise<string> {
  console.log('Getting valid access token...');

  const { data: tokens, error } = await supabase
    .from('google_fit_tokens')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    console.error('Error fetching tokens from database:', error);
    throw error;
  }
  if (!tokens) {
    throw new Error('No tokens found');
  }

  const expiryDate = new Date(tokens.expiry_date);
  if (expiryDate <= new Date()) {
    console.log('Token expired, refreshing...');
    const newTokens = await refreshAccessToken(tokens.refresh_token);
    await saveTokens(userId, {
      ...newTokens,
      refresh_token: tokens.refresh_token
    });
    return newTokens.access_token;
  }

  console.log('Using existing valid token');
  return tokens.access_token;
}

// Get heart rate data
export async function getHeartRateData(userId: string, startTime: Date, endTime: Date) {
  console.log('Fetching heart rate data...');

  try {
    const accessToken = await getValidAccessToken(userId);
    const endpoint = `https://www.googleapis.com/fitness/v1/users/me/dataSources/derived:com.google.heart_rate.bpm:com.google.android.gms:merge_heart_rate_bpm/datasets/${startTime.getTime()}000000-${endTime.getTime()}000000`;
    
    console.log('Heart rate request:', {
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString()
    });

    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Heart rate data fetch error:', errorData);
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Heart rate data fetched successfully');

    // Save to Supabase
    const heartRateData = data.point?.map((point: any) => ({
      user_id: userId,
      data_type: 'heart_rate',
      value: {
        bpm: point.value?.[0]?.fpVal || 0
      },
      timestamp: new Date(Number(point.startTimeNanos) / 1000000).toISOString()
    })) || [];

    if (heartRateData.length > 0) {
      console.log('Saving heart rate data to database...');
      await supabase.from('google_fit_data').insert(heartRateData);
    }

    return heartRateData;
  } catch (error) {
    console.error('Error fetching heart rate data:', error);
    throw error;
  }
}

// Get sleep data
export async function getSleepData(userId: string, startDate: Date, endDate: Date) {
  console.log('Fetching sleep data...');

  try {
    const accessToken = await getValidAccessToken(userId);
    const endpoint = `https://www.googleapis.com/fitness/v1/users/me/sessions?startTime=${startDate.toISOString()}&endTime=${endDate.toISOString()}&activityType=72`;
    
    console.log('Sleep data request:', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    const response = await fetch(endpoint, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Sleep data fetch error:', errorData);
      throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Sleep data fetched successfully');

    // Save to Supabase
    const sleepData = data.session?.map((session: any) => ({
      user_id: userId,
      data_type: 'sleep',
      value: {
        start_time: new Date(session.startTimeMillis || 0).toISOString(),
        end_time: new Date(session.endTimeMillis || 0).toISOString(),
        duration: Math.round((Number(session.endTimeMillis) - Number(session.startTimeMillis)) / (60 * 1000))
      },
      timestamp: new Date(session.startTimeMillis || 0).toISOString()
    })) || [];

    if (sleepData.length > 0) {
      console.log('Saving sleep data to database...');
      await supabase.from('google_fit_data').insert(sleepData);
    }

    return sleepData;
  } catch (error) {
    console.error('Error fetching sleep data:', error);
    throw error;
  }
}

// Sync all data
export async function syncData(userId: string) {
  console.log('Starting data sync...');

  try {
    const endTime = new Date();
    const startTime = new Date(endTime.getTime() - 24 * 60 * 60 * 1000); // Last 24 hours

    await Promise.all([
      getHeartRateData(userId, startTime, endTime),
      getSleepData(userId, startTime, endTime)
    ]);

    console.log('Data sync completed successfully');
    return true;
  } catch (error) {
    console.error('Error syncing data:', error);
    throw error;
  }
}